import { uuid } from '@xt/client/utils/uuid'
import { AjaxBasics, Encryption } from '@xt/client/helpers'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiCurrency } from '@xt/client/api'
import { reportEntryLog } from '@xt/client/utils/log'
import md5 from 'crypto-js/md5'
import type {
  UploadItemType,
  StatusChangeEvent,
  UploadPolicy,
  UploadingItem,
  UploadResult,
  UploadFailedItem,
  UploadSuccessItem,
  WaitUploadItem
} from './types.d'

export class ImageUploaderUtils {
  public maxSize: number = 1

  constructor(max: number) {
    this.maxSize = max
  }

  private imageList: UploadItemType[] = []
  private statusChangeListeners: Array<StatusChangeEvent> = []
  private policyCache: null | UploadPolicy = null
  private signals: Map<string, AbortController> = new Map()
  private isUploading = false

  public get size() {
    return this.imageList.length
  }

  public addImage(data: { url: string; size: number; name: string; raw: File }) {
    if (this.size > this.maxSize - 1) {
      return false
    }

    const ext = '.' + data.name.split('.').pop()

    this.imageList.push({
      status: 'wait-upload',
      filename: data.name,
      localFilePath: data.url,
      size: data.size,
      uid: uuid(),
      rawImageFile: data.raw,
      progress: 0,
      ext,
      md5: Encryption.uniqueId()
    })

    this.triggerChangeEvent()

    this.startUploadImage()

    return true
  }

  public removeImage(uid: string) {
    // 删除一个图片 如果当前图片还没有开始上传则直接从队列中移除 否则需要先取消任务
    const item = this.imageList.find(i => i.uid === uid)

    if (!item) return

    const controller = this.signals.get(uid)
    if (controller) {
      console.log('检测到取消')
      controller.abort()
      this.signals.delete(uid)
    }

    this.imageList = this.imageList.filter(i => i.uid !== uid)
    this.triggerChangeEvent()
  }

  public reuploadImage(uid: string) {
    const item = this.imageList.find(i => i.uid === uid)

    if (!item || item.status !== 'fail') return

    const controller = this.signals.get(uid)
    if (controller) {
      console.log('reupload检测到取消')
      controller.abort()
      this.signals.delete(uid)
    }

    this.imageList = this.imageList.map(i => {
      if (i.uid === uid) {
        const { reason, ...rest } = i as UploadFailedItem

        return { ...rest, status: 'wait-upload' } satisfies WaitUploadItem
      }

      return i
    })

    this.startUploadImage()

    this.triggerChangeEvent()
  }

  // one by one
  private async startUploadImage() {
    console.warn('call startUploadImage')
    if (this.isUploading) return

    const item = this.imageList.find(img => img.status === 'wait-upload')

    if (!item) return

    try {
      this.isUploading = true

      const ossPolicy = await this.getPolicy()

      if (!ossPolicy) {
        reportEntryLog('upload-task.policy.fail', {
          queue: this.imageList.map(i => ({ uid: i.uid, status: i.status, name: i.filename, size: i.size }))
        })
        return
      }

      // 修改状态
      this.imageList = this.imageList.map(i => {
        if (i.uid === item.uid) {
          return { ...i, status: 'uploading' } satisfies UploadingItem
        }

        return i
      })

      this.triggerChangeEvent()

      // 触发ajax上传
      const formData = new FormData()
      formData.append('key', ossPolicy.dir + item.md5 + item.ext)
      formData.append('OSSAccessKeyId', ossPolicy.accessKeyId)
      formData.append('policy', ossPolicy.policy)
      formData.append('signature', ossPolicy.signature)
      formData.append('success_action_status', '200')
      formData.append('callback', ossPolicy.callbackBody)
      formData.append('file', item.rawImageFile, item.filename)

      const that = this

      let controller: undefined | AbortController = undefined

      if (window.AbortController) {
        controller = new AbortController()
        this.signals.set(item.uid, controller)
      }

      const [error, data] = await $service<UploadResult>({
        method: 'POST',
        url: ossPolicy.host,
        data: formData,
        signal: controller ? controller.signal : undefined,
        // 超时时间半小时
        timeout: 30 * 60 * 1000,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress(progressEvent) {
          console.log('progressEvent', progressEvent.progress?.toPrecision(2))
          that.imageList = that.imageList.map(i => {
            if (i.uid === item.uid) {
              return { ...i, status: 'uploading', progress: Math.ceil((progressEvent.progress || 0) * 100) } satisfies UploadingItem
            }

            return i
          })

          that.triggerChangeEvent()
        }
      })

      this.signals.delete(item.uid)

      if (error) {
        that.imageList = that.imageList.map(i => {
          if (i.uid === item.uid) {
            return { ...i, status: 'fail', reason: error.errMsg } satisfies UploadFailedItem
          }

          return i
        })

        that.triggerChangeEvent()
        return
      }

      that.imageList = that.imageList.map(i => {
        if (i.uid === item.uid) {
          return {
            ...i,
            status: 'success',
            originUrl: data.originUrl,
            waterUrl: data.waterUrl,
            originalWidth: data.originalWidth,
            originalHeight: data.originalHeight,
            // 将本地base64图片直接替换为远程图片
            localFilePath: data.waterUrl
          } satisfies UploadSuccessItem
        }

        return i
      })

      that.triggerChangeEvent()
    } finally {
      this.isUploading = false
      this.startUploadImage()
    }
  }

  private async getPolicy(): Promise<null | UploadPolicy> {
    if (this.policyCache) {
      // 检查缓存是否有效
      const now = AjaxBasics.serviceDate

      if (now.isBefore(this.policyCache.expire)) {
        return this.policyCache
      }

      this.policyCache = null
    }

    const [error, policy] = await $service<UploadPolicy>({
      method: 'GET',
      url: EnumApiCurrency.files,
      params: { picType: 0, type: 1 }
    })

    if (error) return null

    this.policyCache = policy

    return this.policyCache
  }

  private triggerChangeEvent() {
    this.statusChangeListeners.forEach(callback => {
      callback(this.imageList)
    })
  }

  public addChangeListener(callback: StatusChangeEvent) {
    this.statusChangeListeners.push(callback)
  }

  public removeChangeListener(callback: StatusChangeEvent) {
    this.statusChangeListeners = this.statusChangeListeners.filter(cb => cb !== callback)
  }
}
